import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as DailyReportSearchFormInitialData,
  ValidData as DailyReportSearchFormValidData,
} from '@/components/searchForms/DailyReportSearchForm.vue';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns-tz';
import _isEqual from 'lodash/isEqual';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetAmPmJobs } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { AmPmJobs } from '@/models/reports';

export interface DailyReportViewComposable {
  search: (formData: DailyReportSearchFormValidData) => void;
  initialData: ComputedRef<DailyReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  dailyReportData: Ref<AmPmJobs | undefined>;
}

export default function useDailyReportView(
  queryDate: Readonly<Ref<Date | undefined>>,
): DailyReportViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<DailyReportSearchFormInitialData>(() => {
    let date;
    if (queryDate.value) {
      date = format(queryDate.value, 'yyyy-MM-dd');
    }
    return {
      date,
    };
  });

  const isFetchingData = ref(false);
  const dailyReportData = ref<AmPmJobs | undefined>();

  function search(formData: DailyReportSearchFormValidData): void {
    const query = { ...route.query, ...formData };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.AM_PM_JOBS,
      query,
      force: true,
      replace: isReplace,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    dailyReportData.value = undefined;
    if (!queryDate.value) return;
    isFetchingData.value = true;
    await fetchGetAmPmJobs(userTimeZone.value, queryDate.value)
      .then((data) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }
        dailyReportData.value = data;
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.AM_PM_JOBS) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    dailyReportData,
  };
}
